import { z } from "zod";
export * from "./forensic-preparation";
export * from "./forensic-records";

export const workspaceSchema = z.enum([
  "client",
  "organization_manager",
  "platform_admin",
  "analyst",
  "senior_analyst",
]);

export type Workspace = z.infer<typeof workspaceSchema>;

export const preparedLocaleSchema = z.enum([
  "en",
  "fa",
  "ar",
  "it",
  "ru",
  "es",
  "fr",
  "de",
  "tr",
  "uk",
  "zh-cn",
  "sv",
]);

export type PreparedLocale = z.infer<typeof preparedLocaleSchema>;

export const actorSchema = z.object({
  user_id: z.uuid(),
  tenant_id: z.uuid(),
  workspace: workspaceSchema,
  organization_id: z.uuid().nullable(),
  workspace_home: z.string().startsWith("/"),
  assurance_level: z.enum(["password", "mfa", "phishing_resistant"]),
  requires_password_change: z.boolean(),
  setup_required: z.boolean().default(false),
});

export type Actor = z.infer<typeof actorSchema>;

export const adminPrerequisiteSchema = z.object({
  key: z.string().min(1).max(80),
  label: z.string().min(1).max(120),
  scope: z.enum(["runtime_dependency", "session_safeguard"]),
  enabled: z.boolean(),
  ready: z.boolean(),
  setup_path: z.enum(["/admin/settings", "/admin/auth"]),
  setup_hint: z.string().min(1).max(500),
  affected_areas: z.array(z.string()),
});

export const adminActionAvailabilitySchema = z.object({
  key: z.string().min(1).max(80),
  label: z.string().min(1).max(120),
  enabled: z.boolean(),
  blockers: z.array(z.string()),
});

export const adminActionPrerequisitesSchema = z.object({
  prerequisites: z.array(adminPrerequisiteSchema),
  actions: z.array(adminActionAvailabilitySchema),
});

export type AdminActionPrerequisites = z.infer<typeof adminActionPrerequisitesSchema>;

export const emailDeliveryConfigurationSchema = z.object({
  authentication: z.enum(["none", "password", "oauth2"]),
  host: z.string().min(1).max(253).nullable(),
  port: z.number().int().min(1).max(65535).nullable(),
  sender: z.email(),
  username: z.string().max(254).nullable(),
  security: z.enum(["starttls", "implicit_tls"]).nullable(),
  oauth_provider: z.enum(["google", "microsoft"]).nullable(),
  oauth_client_id: z.string().nullable(),
  credential_configured: z.boolean(),
  verified: z.literal(true),
  verified_at: z.iso.datetime(),
  version: z.number().int().positive(),
});

export type EmailDeliveryConfiguration = z.infer<typeof emailDeliveryConfigurationSchema>;

export const emailOAuthAuthorizationSchema = z.object({
  authorization_url: z.url(),
  expires_at: z.iso.datetime(),
});

export type EmailOAuthAuthorization = z.infer<typeof emailOAuthAuthorizationSchema>;

export const notificationPreferencesSchema = z.object({
  user_id: z.uuid(),
  in_app_enabled: z.literal(true),
  email_enabled: z.boolean(),
  safe_email_masked: z.string().nullable(),
  case_updates: z.boolean(),
  security_alerts: z.boolean(),
  wellness_reminders: z.boolean(),
  timezone: z.string().min(1).max(100),
  locale: preparedLocaleSchema,
  updated_at: z.iso.datetime().nullable(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

export const inAppNotificationSchema = z.object({
  notification_id: z.uuid(),
  category: z.enum(["case", "security", "wellness", "announcement"]),
  message_key: z.enum([
    "case_opened",
    "forensic_report_released",
    "forensic_report_submitted",
    "forensic_report_revision_requested",
    "wellness_report_released",
    "wellness_requested",
    "wellness_assigned",
    "wellness_questionnaire_ready",
    "wellness_analysis_submitted",
    "wellness_revision_requested",
    "case_assigned",
    "account_email_changed",
    "device_revoked",
    "device_reenrollment_requested",
    "helpdesk_reply_available",
    "helpdesk_pgp_key_expiring",
    "admin_announcement",
  ]),
  origin: z.enum(["system", "admin_authored"]).default("system"),
  title: z.string().min(1).max(200).nullable().default(null),
  body: z.string().min(1).max(5000).nullable().default(null),
  created_at: z.iso.datetime(),
  read_at: z.iso.datetime().nullable(),
});
export type InAppNotification = z.infer<typeof inAppNotificationSchema>;

export const accountProfileSchema = z.object({
  user_id: z.uuid(),
  email: z.email().nullable(),
  email_verified_at: z.iso.datetime().nullable(),
  display_name: z.string().min(1).max(200),
  presentation_name: z.string().min(1).max(200).nullable().default(null),
  given_name: z.string().max(120).nullable(),
  family_name: z.string().max(120).nullable(),
  professional_title: z.string().max(200).nullable(),
  phone_number: z.string().max(40).nullable(),
  country_code: z.string().length(2).nullable(),
  country_name: z.string().nullable().default(null),
  locality: z.string().max(160).nullable(),
  locale: preparedLocaleSchema,
  timezone: z.string().min(1).max(100),
  workspace: workspaceSchema,
  active_session_count: z.number().int().nonnegative(),
  pending_email: z.email().nullable(),
  pending_email_expires_at: z.iso.datetime().nullable(),
  bootstrap_account: z.boolean().default(false),
  setup_complete: z.boolean().default(true),
  avatar_mode: z.enum(["initials", "photo"]).default("initials"),
  avatar_url: z.string().nullable().default(null),
  avatar_photo_available: z.boolean().default(false),
  avatar_updated_at: z.iso.datetime().nullable().default(null),
});
export type AccountProfile = z.infer<typeof accountProfileSchema>;

export const ownSessionSchema = z.object({
  session_id: z.uuid(),
  assurance_level: z.enum(["password", "mfa", "phishing_resistant"]),
  created_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
  current: z.boolean(),
});
export type OwnSession = z.infer<typeof ownSessionSchema>;

export const countryLocationSchema = z.object({
  code: z.string().length(2),
  name: z.string().min(1),
});
export type CountryLocation = z.infer<typeof countryLocationSchema>;

export const cityLocationSchema = z.object({
  name: z.string().min(1),
  country_code: z.string().length(2),
});
export type CityLocation = z.infer<typeof cityLocationSchema>;

export const emailChangePendingSchema = z.object({
  request_id: z.uuid(),
  delivery_id: z.uuid(),
  pending_email: z.email(),
  expires_at: z.iso.datetime(),
});
export type EmailChangePending = z.infer<typeof emailChangePendingSchema>;

export const adminAnnouncementSchema = z.object({
  announcement_id: z.uuid(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(5000),
  recipient_count: z.number().int().positive(),
  delivery_channels: z.array(z.enum(["in_app", "email", "mobile_push"])),
  created_at: z.iso.datetime(),
});
export type AdminAnnouncement = z.infer<typeof adminAnnouncementSchema>;

export const loginResponseSchema = z.object({
  actor: actorSchema,
  expires_at: z.iso.datetime(),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const activationPreviewSchema = z.object({
  display_name: z.string().min(1).max(200),
  email: z.email(),
  workspace: workspaceSchema,
  expires_at: z.iso.datetime(),
});
export type ActivationPreview = z.infer<typeof activationPreviewSchema>;

export const accountSetupStatusSchema = z.object({
  origin: z.enum(["invitation", "bootstrap"]),
  display_name: z.string().min(1).max(200),
  email: z.email().nullable(),
  email_verified: z.boolean(),
  workspace: workspaceSchema,
  workspace_home: z.string().startsWith("/"),
  password_set: z.boolean(),
  authenticator_configured: z.boolean(),
  recovery_codes_issued: z.boolean(),
  recovery_codes_acknowledged: z.boolean(),
  ready_to_complete: z.boolean(),
});
export type AccountSetupStatus = z.infer<typeof accountSetupStatusSchema>;

export const totpEnrollmentSchema = z.object({
  factor_id: z.uuid(),
  provisioning_uri: z.string().startsWith("otpauth://totp/"),
});
export type TotpEnrollment = z.infer<typeof totpEnrollmentSchema>;

export const recoveryCodesSchema = z.object({
  recovery_codes: z.array(z.string()).length(10),
});

export const mfaStatusSchema = z.object({
  totp_configured: z.boolean(),
  totp_label: z.string().min(1).max(120).nullable(),
  totp_verified_at: z.iso.datetime().nullable(),
  recovery_codes_remaining: z.number().int().nonnegative(),
  passkey_count: z.number().int().nonnegative(),
});
export type MfaStatus = z.infer<typeof mfaStatusSchema>;

export const passkeyOptionsSchema = z.object({
  challenge_id: z.uuid(),
  public_key: z.record(z.string(), z.unknown()),
  expires_at: z.iso.datetime(),
});
export type PasskeyOptions = z.infer<typeof passkeyOptionsSchema>;

export const passkeyCredentialSchema = z.object({
  passkey_id: z.uuid(),
  label: z.string(),
  device_type: z.string(),
  backed_up: z.boolean(),
});
export type PasskeyCredential = z.infer<typeof passkeyCredentialSchema>;

export const federationStartSchema = z.object({
  authorization_url: z.url(),
  expires_at: z.iso.datetime(),
});
export type FederationStart = z.infer<typeof federationStartSchema>;

export const identityProviderSchema = z.object({
  provider_id: z.uuid(),
  slug: z.string(),
  display_name: z.string(),
  kind: z.enum(["google", "organization_oidc"]),
  issuer: z.url(),
  organization_id: z.uuid().nullable(),
  status: z.enum(["active", "disabled"]),
});
export type IdentityProvider = z.infer<typeof identityProviderSchema>;

export const apiErrorSchema = z.object({
  detail: z.string(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

export const organizationLifecycleSchema = z.enum(["active", "suspended", "archived"]);
export const organizationTypeSchema = z.enum([
  "media",
  "civil_society",
  "human_rights",
  "research",
  "other",
]);
export const loginMethodSchema = z.enum(["password", "sso"]);

export const organizationSchema = z.object({
  organization_id: z.uuid(),
  slug: z.string(),
  display_name: z.string(),
  organization_type: organizationTypeSchema,
  country_code: z.string().length(2),
  locality: z.string().nullable(),
  lifecycle: organizationLifecycleSchema,
  allowed_login_methods: z.array(loginMethodSchema),
  verified_domains: z.array(z.string()),
  logo_url: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  member_count: z.number().int().nonnegative().default(0),
  manager_names: z.array(z.string()).default([]),
  pending_setup: z.boolean().default(false),
  pending_invitation_count: z.number().int().nonnegative().default(0),
});
export type Organization = z.infer<typeof organizationSchema>;

export const membershipSchema = z.object({
  membership_id: z.uuid(),
  user_id: z.uuid(),
  email: z.email(),
  display_name: z.string(),
  role: z.enum(["manager", "member"]),
  status: z.enum(["active", "suspended", "revoked"]),
});

export const pendingInvitationSchema = z.object({
  invitation_id: z.uuid(),
  email: z.email(),
  display_name: z.string(),
  workspace: workspaceSchema,
  status: z.string(),
  expires_at: z.iso.datetime(),
});

export const organizationSnapshotSchema = z.object({
  organization: organizationSchema,
  members: z.array(membershipSchema),
  invitations: z.array(pendingInvitationSchema),
});
export type OrganizationSnapshot = z.infer<typeof organizationSnapshotSchema>;

export const consentNoticeSchema = z.object({
  notice_id: z.uuid(),
  version: z.string(),
  locale: preparedLocaleSchema,
  body_markdown: z.string(),
  body_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  scopes: z.array(z.string()).min(1),
  published_at: z.iso.datetime(),
});
export type ConsentNotice = z.infer<typeof consentNoticeSchema>;

export const caseKindSchema = z.enum([
  "forensic_collection",
  "suspicious_url",
  "suspicious_file",
  "wellness_check",
  "mobile_deep_scan",
  "mobile_security",
]);
export const caseRecordSchema = z.object({
  case_id: z.uuid(),
  owner_user_id: z.uuid(),
  organization_id: z.uuid().nullable(),
  kind: caseKindSchema,
  status: z.enum(["open", "consent_withdrawn", "closed", "disposed"]),
  title: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type CaseRecord = z.infer<typeof caseRecordSchema>;

export const caseIntakeSchema = z.object({
  case_intake_id: z.uuid(),
  kind: caseKindSchema,
  description: z.string().nullable(),
  suspicious_url: z.string().nullable(),
  original_filename: z.string().nullable(),
  email_context: z.string().nullable(),
  created_at: z.iso.datetime(),
});

export const consentReceiptSchema = z.object({
  consent_receipt_id: z.uuid(),
  notice_id: z.uuid(),
  notice_version: z.string(),
  notice_locale: preparedLocaleSchema,
  notice_body_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  scopes: z.array(z.string()),
  granted_at: z.iso.datetime(),
  withdrawn_at: z.iso.datetime().nullable(),
});

export const retentionScheduleSchema = z.object({
  retention_schedule_id: z.uuid(),
  policy_version: z.string(),
  status: z.enum(["scheduled", "held", "deletion_pending", "disposed"]),
  retain_until: z.iso.datetime(),
  hold_reason: z.string().nullable(),
  disposed_at: z.iso.datetime().nullable(),
});

export const collectionRequestSchema = z.object({
  collection_request_id: z.uuid(),
  device_id: z.uuid(),
  requested_by_user_id: z.uuid(),
  level: z.enum(["metadata", "extended", "memory"]),
  time_range_start: z.iso.datetime(),
  time_range_end: z.iso.datetime(),
  contract_version: z.literal("collection-request.v1"),
  status: z.enum(["authorized", "cancelled"]),
  created_at: z.iso.datetime(),
  cancelled_at: z.iso.datetime().nullable(),
});

export const deviceSchema = z.object({
  device_id: z.uuid(),
  ownership: z.enum(["personal", "organization"]),
  platform: z.enum(["windows", "macos", "linux", "android", "ios", "unknown"]),
  label: z.string(),
  status: z.enum(["active", "revoked", "pending"]),
  owner_user_id: z.uuid().nullable(),
  organization_id: z.uuid().nullable(),
  assigned_user_id: z.uuid().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  revoked_at: z.iso.datetime().nullable(),
});
export type Device = z.infer<typeof deviceSchema>;

export const caseSnapshotSchema = z.object({
  case: caseRecordSchema,
  intake: caseIntakeSchema,
  consent: consentReceiptSchema,
  retention: retentionScheduleSchema,
  collection_request: collectionRequestSchema.nullable(),
});
export type CaseSnapshot = z.infer<typeof caseSnapshotSchema>;

export const caseAssignmentSchema = z.object({
  assignment_id: z.uuid(),
  case_id: z.uuid(),
  senior_reviewer_id: z.uuid(),
  analyst_user_id: z.uuid(),
  assigned_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type CaseAssignment = z.infer<typeof caseAssignmentSchema>;

export const evidenceSummarySchema = z.object({
  evidence_id: z.uuid(),
  case_id: z.uuid(),
  size: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  admitted_at: z.iso.datetime(),
  disposition_status: z.enum(["active", "deletion_pending", "blocked", "disposed"]),
});
export type EvidenceSummary = z.infer<typeof evidenceSummarySchema>;

export const analysisToolSchema = z.object({
  tool_version_id: z.uuid(),
  name: z.string(),
  version: z.string(),
  contract_version: z.literal("kavoosh-analysis-plugin.v1"),
  image_digest: z.string().regex(/^sha256:[0-9a-f]{64}$/),
  result_schema_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  status: z.enum(["active", "retired"]),
  created_at: z.iso.datetime(),
});
export type AnalysisTool = z.infer<typeof analysisToolSchema>;

export const analysisRunSchema = z.object({
  analysis_run_id: z.uuid(),
  case_id: z.uuid(),
  evidence_id: z.uuid(),
  analyst_user_id: z.uuid(),
  senior_reviewer_id: z.uuid(),
  tool_version_id: z.uuid(),
  source_evidence_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  requested_at: z.iso.datetime(),
  started_at: z.iso.datetime().nullable(),
  completed_at: z.iso.datetime().nullable(),
  result_manifest_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  failure_code: z.string().nullable(),
});
export type AnalysisRun = z.infer<typeof analysisRunSchema>;

export const forensicFindingSchema = z.object({
  finding_id: z.uuid(),
  case_id: z.uuid(),
  evidence_id: z.uuid(),
  analysis_run_id: z.uuid(),
  title: z.string(),
  summary: z.string(),
  severity: z.enum(["informational", "low", "medium", "high", "critical"]),
  confidence: z.number().int().min(0).max(100),
  status: z.enum(["candidate", "analyst_confirmed", "senior_approved", "rejected"]),
  created_at: z.iso.datetime(),
  analyst_confirmed_at: z.iso.datetime().nullable(),
  reviewed_at: z.iso.datetime().nullable(),
  reviewed_by: z.uuid().nullable(),
  review_reason: z.string().nullable(),
});
export type ForensicFinding = z.infer<typeof forensicFindingSchema>;

export const forensicReportSchema = z.object({
  report_id: z.uuid(),
  case_id: z.uuid(),
  version: z.number().int().positive(),
  contract_version: z.literal("kavoosh-forensic-report.v1"),
  title: z.string(),
  executive_summary: z.string(),
  status: z.enum(["draft", "submitted", "released", "rejected"]),
  created_by: z.uuid(),
  created_at: z.iso.datetime(),
  submitted_at: z.iso.datetime().nullable(),
  reviewed_at: z.iso.datetime().nullable(),
  reviewed_by: z.uuid().nullable(),
  review_reason: z.string().nullable(),
  released_to_user_id: z.uuid().nullable(),
  manifest_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  signing_key_id: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
});
export type ForensicReport = z.infer<typeof forensicReportSchema>;

export const forensicReportWorkspaceCaseSchema = z.object({
  case: caseRecordSchema,
  assignment: z.object({
    assignment_id: z.uuid(),
    case_id: z.uuid(),
    senior_reviewer_id: z.uuid(),
    analyst_user_id: z.uuid(),
    assigned_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
  }),
  approved_findings: z.array(forensicFindingSchema),
  latest_report: forensicReportSchema.nullable(),
});
export type ForensicReportWorkspaceCase = z.infer<typeof forensicReportWorkspaceCaseSchema>;

const forensicReportArtifactSnapshotSchema = z.object({
  artifact_id: z.uuid(),
  kind: z.string(),
  media_type: z.string(),
  size: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  source_evidence_sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const forensicReportManifestSchema = z.object({
  contract_version: z.literal("kavoosh-forensic-report.v1"),
  report_id: z.uuid(),
  case_id: z.uuid(),
  case_title: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  executive_summary: z.string(),
  released_to_user_id: z.uuid(),
  released_at: z.iso.datetime(),
  reviewed_by: z.uuid(),
  review_reason: z.string(),
  findings: z.array(z.object({
    finding_id: z.uuid(),
    title: z.string(),
    summary: z.string(),
    severity: z.enum(["informational", "low", "medium", "high", "critical"]),
    confidence: z.number().int().min(0).max(100),
    analyst_confirmed_at: z.iso.datetime(),
    senior_reviewed_at: z.iso.datetime(),
    senior_reviewed_by: z.uuid(),
    senior_review_reason: z.string(),
    citations: z.array(z.object({
      locator: z.string(),
      cited_content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
      artifact: forensicReportArtifactSnapshotSchema,
    })).min(1),
  })).min(1),
});
export type ForensicReportManifest = z.infer<typeof forensicReportManifestSchema>;

export const releasedForensicReportSchema = z.object({
  report: forensicReportSchema,
  manifest: forensicReportManifestSchema,
  signature_b64: z.string().regex(/^[A-Za-z0-9_-]+$/),
  public_key_b64: z.string().regex(/^[A-Za-z0-9_-]+$/),
});
export type ReleasedForensicReport = z.infer<typeof releasedForensicReportSchema>;

export const wellnessSubjectSchema = z.enum(["person", "organization"]);
export const wellnessDeliveryModeSchema = z.enum(["online", "interview"]);
export const wellnessProfileSchema = z.enum(["full", "lite"]);
export const wellnessLevelSchema = z.enum([
  "baseline",
  "evolving",
  "intermediate",
  "advanced",
  "innovative",
]);
export const wellnessAssessmentStatusSchema = z.enum([
  "submitted",
  "assigned",
  "awaiting_response",
  "response_submitted",
  "analysis",
  "senior_review",
  "revision_requested",
  "report_ready",
  "cancelled",
]);

export const questionnaireOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});
export const questionnaireQuestionSchema = z.object({
  question_id: z.string(),
  prompt: z.string(),
  response_type: z.enum([
    "short_text",
    "long_text",
    "boolean",
    "single_choice",
    "multi_choice",
  ]),
  required: z.boolean(),
  help_text: z.string().nullable(),
  options: z.array(questionnaireOptionSchema),
});
export type QuestionnaireQuestion = z.infer<typeof questionnaireQuestionSchema>;
export const questionnaireSectionSchema = z.object({
  section_id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  questions: z.array(questionnaireQuestionSchema),
});
export const questionnaireVersionSchema = z.object({
  questionnaire_version_id: z.uuid(),
  target: wellnessSubjectSchema,
  version: z.string(),
  locale: preparedLocaleSchema,
  title: z.string(),
  status: z.enum(["draft", "published", "retired"]),
  sections: z.array(questionnaireSectionSchema),
  schema_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  created_by_user_id: z.uuid(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  published_at: z.iso.datetime().nullable(),
  retired_at: z.iso.datetime().nullable(),
});
export type QuestionnaireVersion = z.infer<typeof questionnaireVersionSchema>;

export const wellnessAssessmentSchema = z.object({
  assessment_id: z.uuid(),
  case_id: z.uuid(),
  subject_type: wellnessSubjectSchema,
  subject_user_id: z.uuid().nullable(),
  subject_organization_id: z.uuid().nullable(),
  delivery_mode: wellnessDeliveryModeSchema,
  intake_profile: wellnessProfileSchema,
  questionnaire_version_id: z.uuid(),
  questionnaire_version: z.string(),
  status: wellnessAssessmentStatusSchema,
  requested_by_user_id: z.uuid(),
  requested_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  senior_analyst_id: z.uuid().nullable(),
  assigned_analyst_id: z.uuid().nullable(),
  unlocked_at: z.iso.datetime().nullable(),
  response_submitted_at: z.iso.datetime().nullable(),
  analysis_submitted_at: z.iso.datetime().nullable(),
  report_released_at: z.iso.datetime().nullable(),
});
export type WellnessAssessment = z.infer<typeof wellnessAssessmentSchema>;

export const wellnessResponseSchema = z.object({
  response_id: z.uuid(),
  assessment_id: z.uuid(),
  questionnaire_version_id: z.uuid(),
  questionnaire_version: z.string(),
  respondent_user_id: z.uuid(),
  recorded_by_user_id: z.uuid(),
  answers: z.record(z.string(), z.unknown()),
  submitted_at: z.iso.datetime(),
});

export const wellnessRatingSchema = z.object({
  criterion_key: z.string(),
  rating: z.enum(["not_applicable", "low", "moderately_low", "moderate", "high"]),
  explanation: z.string(),
});
export const wellnessCriterionSchema = z.object({
  criterion_key: z.string(),
  label: z.string(),
  description: z.string(),
});
export type WellnessCriterion = z.infer<typeof wellnessCriterionSchema>;

export const wellnessAnalysisSchema = z.object({
  analysis_id: z.uuid(),
  assessment_id: z.uuid(),
  rule_set_version: z.string(),
  automatic_ratings: z.array(wellnessRatingSchema),
  automatic_score: z.number().int().nonnegative(),
  automatic_level: wellnessLevelSchema,
  final_ratings: z.array(wellnessRatingSchema),
  final_score: z.number().int().nonnegative(),
  final_level: wellnessLevelSchema,
  override_reasons: z.record(z.string(), z.string()),
  summary: z.string(),
  recommendations: z.array(z.string()),
  milestones: z.array(z.string()),
  analyst_user_id: z.uuid(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  submitted_at: z.iso.datetime().nullable(),
  revision_note: z.string().nullable(),
});

export const wellnessReportSchema = z.object({
  report_id: z.uuid(),
  assessment_id: z.uuid(),
  case_id: z.uuid(),
  subject_type: wellnessSubjectSchema,
  subject_user_id: z.uuid().nullable(),
  subject_organization_id: z.uuid().nullable(),
  questionnaire_version: z.string(),
  rule_set_version: z.string(),
  score: z.number().int().nonnegative(),
  level: wellnessLevelSchema,
  summary: z.string(),
  recommendations: z.array(z.string()),
  milestones: z.array(z.string()),
  released_by_user_id: z.uuid(),
  released_at: z.iso.datetime(),
});
export type WellnessReport = z.infer<typeof wellnessReportSchema>;

export const wellnessSnapshotSchema = z.object({
  assessment: wellnessAssessmentSchema,
  case: caseRecordSchema,
  questionnaire: questionnaireVersionSchema,
  response: wellnessResponseSchema.nullable(),
  analysis: wellnessAnalysisSchema.nullable(),
  report: wellnessReportSchema.nullable(),
});
export type WellnessSnapshot = z.infer<typeof wellnessSnapshotSchema>;

export const operationalUserSchema = z.object({
  user_id: z.uuid(),
  display_name: z.string(),
  workspace: workspaceSchema,
  status: z.enum([
    "pending", "active", "risk_locked", "suspended", "disabled",
    "deletion_pending", "disposed",
  ]),
  organization_id: z.uuid().nullable(),
});
export type OperationalUser = z.infer<typeof operationalUserSchema>;

export const adminMembershipSchema = z.object({
  membership_id: z.uuid(),
  organization_id: z.uuid(),
  organization_name: z.string().min(1),
  role: z.enum(["manager", "member"]),
  status: z.enum(["active", "suspended", "revoked"]),
  created_at: z.iso.datetime(),
});
export type AdminMembership = z.infer<typeof adminMembershipSchema>;

export const adminUserSchema = z.object({
  user_id: z.uuid(),
  email: z.email().nullable(),
  display_name: z.string(),
  email_verified_at: z.iso.datetime().nullable().default(null),
  given_name: z.string().max(120).nullable().default(null),
  family_name: z.string().max(120).nullable().default(null),
  professional_title: z.string().max(200).nullable().default(null),
  phone_number: z.string().max(40).nullable().default(null),
  country_code: z.string().length(2).nullable().default(null),
  locality: z.string().max(160).nullable().default(null),
  timezone: z.string().min(1).max(100).default("UTC"),
  workspace: workspaceSchema,
  status: z.enum([
    "pending", "active", "risk_locked", "suspended", "disabled",
    "deletion_pending", "disposed",
  ]),
  organization_id: z.uuid().nullable(),
  locale: preparedLocaleSchema,
  requires_password_change: z.boolean(),
  mfa_configured: z.boolean(),
  passkey_count: z.number().int().nonnegative(),
  active_session_count: z.number().int().nonnegative(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  last_active_at: z.iso.datetime().nullable().default(null),
  avatar_mode: z.enum(["initials", "photo"]).default("initials"),
  avatar_url: z.string().nullable().default(null),
  memberships: z.array(adminMembershipSchema).default([]),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminInvitationSchema = z.object({
  invitation_id: z.uuid(),
  email: z.email(),
  display_name: z.string(),
  workspace: workspaceSchema,
  organization_id: z.uuid().nullable(),
  status: z.enum(["pending", "in_progress", "consumed", "expired", "revoked"]),
  locale: preparedLocaleSchema,
  expires_at: z.iso.datetime(),
  created_at: z.iso.datetime(),
  delivery_id: z.uuid(),
  delivery_status: z.enum([
    "queued", "sent", "retry_scheduled", "failed", "bounced", "suppressed",
  ]),
  delivery_attempt_count: z.number().int().nonnegative(),
  delivery_last_error_code: z.string().nullable(),
});
export type AdminInvitation = z.infer<typeof adminInvitationSchema>;

export const adminSessionSchema = z.object({
  session_id: z.uuid(),
  user_id: z.uuid(),
  assurance_level: z.enum(["password", "mfa", "phishing_resistant"]),
  created_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
  elevated_until: z.iso.datetime().nullable(),
  revoked_at: z.iso.datetime().nullable(),
  last_seen_at: z.iso.datetime().nullable().default(null),
});
export type AdminSession = z.infer<typeof adminSessionSchema>;

export const adminRecordKindSchema = z.enum([
  "access_review", "import_job", "risk_configuration", "intelligence_integration",
  "content_version", "policy_version", "locale_configuration", "platform_setting",
  "device_reenrollment", "wellness_program", "collector_release",
  "collector_release_ingestion", "collector_release_artifact_intake",
]);
export const adminRecordStatusSchema = z.enum([
  "draft", "active", "completed", "failed", "revoked", "superseded",
]);
export const adminRecordSchema = z.object({
  record_id: z.uuid(),
  kind: adminRecordKindSchema,
  key: z.string(),
  version: z.number().int().positive(),
  status: adminRecordStatusSchema,
  payload: z.record(z.string(), z.unknown()),
  secret_configured: z.boolean(),
  created_by_user_id: z.uuid(),
  created_at: z.iso.datetime(),
});
export type AdminRecord = z.infer<typeof adminRecordSchema>;

export const adminCaseSchema = z.object({
  case_id: z.uuid(),
  kind: z.string(),
  status: z.string(),
  owner_display_name: z.string(),
  organization_display_name: z.string().nullable(),
  analyst_display_name: z.string().nullable(),
  reviewer_display_name: z.string().nullable(),
  consent_version: z.string().nullable(),
  consent_active: z.boolean(),
  retention_status: z.string().nullable(),
  retain_until: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AdminCase = z.infer<typeof adminCaseSchema>;

export const adminAuditEventSchema = z.object({
  event_id: z.uuid(),
  event_type: z.string(),
  occurred_at: z.iso.datetime(),
  actor_user_id: z.uuid().nullable(),
  subject_type: z.string(),
  subject_id: z.string(),
  outcome: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  metadata_redacted: z.boolean(),
  event_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
});
export type AdminAuditEvent = z.infer<typeof adminAuditEventSchema>;

export const adminAuditSearchSchema = z.object({
  items: adminAuditEventSchema.array(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  has_more: z.boolean(),
  next_cursor_occurred_at: z.iso.datetime().nullable(),
  next_cursor_event_id: z.uuid().nullable(),
});
export type AdminAuditSearchResult = z.infer<typeof adminAuditSearchSchema>;

export const adminAuditFacetsSchema = z.object({
  event_types: z.string().array(),
  subject_types: z.string().array(),
  outcomes: z.string().array(),
});
export type AdminAuditFacets = z.infer<typeof adminAuditFacetsSchema>;

export const adminAuditAppliedFiltersSchema = z.object({
  query: z.string().nullable(),
  event_type: z.string().nullable(),
  subject_type: z.string().nullable(),
  subject_id: z.string().nullable(),
  outcome: z.string().nullable(),
  actor_user_id: z.uuid().nullable(),
  system_only: z.boolean(),
  occurred_from: z.iso.datetime().nullable(),
  occurred_to: z.iso.datetime().nullable(),
});
export type AdminAuditAppliedFilters = z.infer<typeof adminAuditAppliedFiltersSchema>;

export const adminAuditExportSchema = z.object({
  contract_version: z.literal("admin-audit-export.v1"),
  generated_at: z.iso.datetime(),
  filters: adminAuditAppliedFiltersSchema,
  returned_count: z.number().int().nonnegative(),
  truncated: z.boolean(),
  events: adminAuditEventSchema.array(),
});
export type AdminAuditExport = z.infer<typeof adminAuditExportSchema>;

export const communicationDeliverySchema = z.object({
  delivery_id: z.uuid(),
  kind: z.enum(["invitation", "password_recovery", "operational_notification"]),
  status: z.enum(["queued", "sent", "retry_scheduled", "failed", "bounced", "suppressed"]),
  attempt_count: z.number().int().nonnegative(),
  next_attempt_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  locale: preparedLocaleSchema,
  template_version: z.string(),
  last_error_code: z.string().nullable(),
});
export type CommunicationDelivery = z.infer<typeof communicationDeliverySchema>;

export const mobileObservableKindSchema = z.enum([
  "domain",
  "ip",
  "url",
  "file_sha256",
  "package_name",
  "app_signer_sha256",
  "certificate_sha256",
  "spki_sha256",
]);
export const mobileObservableSourceSchema = z.enum([
  "user_input",
  "selected_file",
  "shared_storage",
  "app_archive",
  "installed_app",
  "tls_peer",
  "trust_store",
  "qr_code",
]);
export const mobilePlatformSchema = z.enum(["android", "ios"]);
export const mobileScanProfileSchema = z.enum(["standard", "deep"]);
export const mobileScanAssessmentStateSchema = z.enum([
  "signals_found",
  "no_known_signal",
  "inconclusive",
]);
export const mobileScanSignalStateSchema = z.enum([
  "context_available",
  "context_partial",
  "no_match",
  "unavailable",
]);

export const mobileCapabilitySchema = z.object({
  key: z.enum([
    "identity_session", "cases", "wellness", "in_app_notifications",
    "quick_security_check", "suspicious_object_intake", "forensic_collection",
    "threat_bundle_match", "threat_intelligence_broker", "mobile_security_scan",
    "mobile_deep_scan_v2", "mobile_device_scan_v1", "mobile_security_scan_v2", "helpdesk_gateway", "push_alerts",
  ]),
  state: z.enum(["available", "contract_only", "external_handoff", "unavailable"]),
  reason_code: z.string().min(1).max(80),
});

export const mobileHelpdeskLimitsSchema = z.object({
  contract_version: z.literal("mobile-helpdesk-policy.v1"),
  version: z.number().int().positive().nullable(),
  max_message_bytes: z.number().int().positive().max(512 * 1024),
  max_attachment_count: z.number().int().positive().max(16),
  max_attachment_bytes: z.number().int().positive().max(16 * 1024 * 1024),
  max_total_attachment_bytes: z.number().int().positive().max(16 * 1024 * 1024),
});
export type MobileHelpdeskLimits = z.infer<typeof mobileHelpdeskLimitsSchema>;

export const mobileBootstrapSchema = z.object({
  contract_version: z.literal("mobile-bootstrap.v1"),
  platform: mobilePlatformSchema,
  app_version: z.string().min(1).max(80),
  client_only: z.literal(true),
  locales: z.tuple([z.literal("en"), z.literal("fa")]),
  themes: z.tuple([z.literal("system"), z.literal("light"), z.literal("dark")]),
  direct_helpdesk_access: z.literal(false),
  direct_threat_intelligence_access: z.literal(false),
  helpdesk_logo_url: z.string().max(2048).nullable(),
  helpdesk_limits: mobileHelpdeskLimitsSchema,
  capabilities: z.array(mobileCapabilitySchema),
  security_scan_profiles: z.array(mobileScanProfileSchema),
  security_scan_observable_kinds: z.array(mobileObservableKindSchema),
});
export type MobileBootstrap = z.infer<typeof mobileBootstrapSchema>;

export const mobilePushRegistrationRequestSchema = z.object({
  device_id: z.uuid(),
  token: z.string().min(16).max(16384),
});
export type MobilePushRegistrationRequest = z.infer<
  typeof mobilePushRegistrationRequestSchema
>;

export const mobilePushRegistrationResponseSchema = z.object({
  registration_id: z.uuid(),
  device_id: z.uuid(),
  platform: mobilePlatformSchema,
  created_at: z.iso.datetime(),
});
export type MobilePushRegistrationResponse = z.infer<
  typeof mobilePushRegistrationResponseSchema
>;

export const mobileSecurityScanObservableSchema = z.object({
  client_observable_id: z.uuid(),
  kind: mobileObservableKindSchema,
  source: mobileObservableSourceSchema,
  value: z.string().min(1).max(4096),
});
export const mobileSecurityScanRequestSchema = z.object({
  contract_version: z.literal("mobile-security-scan.v1"),
  platform: mobilePlatformSchema,
  profile: mobileScanProfileSchema,
  observables: z.array(mobileSecurityScanObservableSchema).min(1).max(2000),
});
export type MobileSecurityScanRequest = z.infer<typeof mobileSecurityScanRequestSchema>;

export const mobileSecurityScanSignalSchema = z.object({
  provider: z.string().min(1).max(80),
  state: mobileScanSignalStateSchema,
  contextual_object_id: z.uuid().nullable(),
  result_count: z.number().int().nonnegative().nullable(),
  reliability: z.string().max(500).nullable(),
  queried_at: z.iso.datetime().nullable(),
  request_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  response_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  cache_disposition: z.string().min(1).max(40).nullable(),
});
export const mobileSecurityScanObservableResultSchema = z.object({
  client_observable_id: z.uuid(),
  kind: mobileObservableKindSchema,
  source: mobileObservableSourceSchema,
  normalized_value_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  state: mobileScanAssessmentStateSchema,
  signals: z.array(mobileSecurityScanSignalSchema).max(16),
});
export const mobileSecurityScanResponseSchema = z.object({
  scan_id: z.uuid(),
  contract_version: z.literal("mobile-security-scan.v1"),
  platform: mobilePlatformSchema,
  profile: mobileScanProfileSchema,
  state: mobileScanAssessmentStateSchema,
  observables: z.array(mobileSecurityScanObservableResultSchema).max(2000),
});
export type MobileSecurityScanResponse = z.infer<typeof mobileSecurityScanResponseSchema>;

export const mobileScanContextCategorySchema = z.enum([
  "device_details", "os_details", "network_connection", "network_security",
  "location_coarse", "location_precise", "device_integrity", "scan_environment",
]);
export const mobileScanContextChoiceSchema = z.object({
  category: mobileScanContextCategorySchema,
  admin_enabled: z.boolean(),
  user_allowed: z.boolean(),
  os_allowed: z.boolean(),
  supported: z.boolean(),
  available: z.boolean(),
});
export const mobileSecurityScanDurableRequestSchema = z.object({
  contract_version: z.literal("mobile-security-scan.v2"),
  platform: mobilePlatformSchema,
  profile: mobileScanProfileSchema,
  device_id: z.uuid(),
  idempotency_key: z.string().min(16).max(200).regex(/^[!-~]+$/),
  policy_version: z.string().min(1).max(120),
  consent_record_id: z.uuid(),
  app_version: z.string().min(1).max(120),
  build_number: z.string().min(1).max(120),
  context_decided_at: z.iso.datetime({ offset: true }),
  context_choices: z.array(mobileScanContextChoiceSchema).min(1).max(8),
  observables: z.array(mobileSecurityScanObservableSchema).min(1).max(2000),
}).refine(
  ({ context_choices }) =>
    new Set(context_choices.map(({ category }) => category)).size === context_choices.length,
  { path: ["context_choices"], message: "duplicate scan context category" },
);
export type MobileSecurityScanDurableRequest = z.infer<
  typeof mobileSecurityScanDurableRequestSchema
>;

export const mobileScanHistoryIdsSchema = z.object({
  contract_version: z.literal("mobile-security-scan-history.v1"),
  case_id: z.uuid(),
  device_id: z.uuid(),
  scan_ids: z.array(z.uuid()).max(100),
});
export type MobileScanHistoryIds = z.infer<typeof mobileScanHistoryIdsSchema>;

export const mobileScanHistoryEntrySchema = z.object({
  scan_id: z.uuid(),
  profile: mobileScanProfileSchema,
  state: z.enum(["completed", "partial", "failed", "cancelled"]),
  assessment: mobileScanAssessmentStateSchema.nullable(),
  completed_at: z.iso.datetime({ offset: true }),
  baseline_scan_id: z.uuid().nullable(),
});
export const mobileScanHistoryPageSchema = z.object({
  contract_version: z.literal("mobile-security-scan-history-page.v1"),
  case_id: z.uuid(),
  device_id: z.uuid(),
  entries: z.array(mobileScanHistoryEntrySchema).max(100),
  next_cursor: z.uuid().nullable(),
});
export type MobileScanHistoryPage = z.infer<typeof mobileScanHistoryPageSchema>;

export const collectorCredentialSchema = z.object({
  credential_id: z.uuid(),
  device_id: z.uuid(),
  key_version: z.number().int().positive(),
  public_key_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  agent_instance_id: z.uuid(),
  collector_version: z.string().min(1).max(80),
  status: z.enum(["active", "rotated", "revoked"]),
  created_at: z.iso.datetime(),
});
export type CollectorCredential = z.infer<typeof collectorCredentialSchema>;

export const collectorExecutionManifestSchema = z.object({
  authorization_id: z.uuid(),
  case_id: z.uuid(),
  collection_contract_version: z.literal("collection-request.v1"),
  collection_level: z.enum(["metadata", "extended", "memory"]),
  collection_request_id: z.uuid(),
  contract_version: z.literal("collector-execution-authorization.v1"),
  credential_id: z.uuid(),
  device_id: z.uuid(),
  expires_at: z.iso.datetime(),
  issued_at: z.iso.datetime(),
  tenant_id: z.uuid(),
  time_range_end: z.iso.datetime(),
  time_range_start: z.iso.datetime(),
});
export type CollectorExecutionManifest = z.infer<typeof collectorExecutionManifestSchema>;

export const collectorExecutionAuthorizationSchema = z.object({
  authorization_id: z.uuid(),
  contract_version: z.literal("collector-execution-authorization.v1"),
  manifest: collectorExecutionManifestSchema,
  manifest_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  signing_key_id: z.string().regex(/^[0-9a-f]{64}$/),
  signature_b64: z.string().min(80).max(120),
  issued_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
});
export type CollectorExecutionAuthorization = z.infer<
  typeof collectorExecutionAuthorizationSchema
>;

export const collectorAuthorizationKeySchema = z.object({
  contract_version: z.literal("collector-authorization-key.v1"),
  algorithm: z.literal("Ed25519"),
  key_id: z.string().regex(/^[0-9a-f]{64}$/),
  public_key_b64: z.string().min(40).max(80),
});

export const collectorAttestationClaimsSchema = z.object({
  agent_instance_id: z.uuid(),
  authorization_id: z.uuid(),
  collector_build_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  collector_version: z.string().min(1).max(80),
  contract_version: z.literal("collector-attestation.v1"),
  credential_id: z.uuid(),
  hardware_backed_key: z.boolean().nullable(),
  nonce: z.string().min(16).max(200),
  os_version: z.string().min(1).max(120),
  platform: z.enum(["windows", "macos", "linux", "android", "ios"]),
  secure_boot: z.boolean().nullable(),
  signed_at: z.iso.datetime(),
});

export const collectorStartGrantSchema = z.object({
  attestation_id: z.uuid(),
  attestation_trust_level: z.literal("software_signed_unverified"),
  authorization_id: z.uuid(),
  contract_version: z.literal("collector-start-grant.v1"),
  credential_id: z.uuid(),
  device_id: z.uuid(),
  redeemed_at: z.iso.datetime(),
  redemption_id: z.uuid(),
  start_before: z.iso.datetime(),
});
export type CollectorStartGrant = z.infer<typeof collectorStartGrantSchema>;

export const collectorStartRedemptionSchema = z.object({
  redemption_id: z.uuid(),
  authorization_id: z.uuid(),
  attestation_id: z.uuid(),
  attestation_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  attestation_trust_level: z.literal("software_signed_unverified"),
  contract_version: z.literal("collector-start-grant.v1"),
  grant: collectorStartGrantSchema,
  grant_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  signing_key_id: z.string().regex(/^[0-9a-f]{64}$/),
  signature_b64: z.string().min(80).max(120),
  redeemed_at: z.iso.datetime(),
  start_before: z.iso.datetime(),
});
export type CollectorStartRedemption = z.infer<typeof collectorStartRedemptionSchema>;

export const evidenceArtifactManifestSchema = z.object({
  path: z.string().min(1).max(500),
  category: z.string().min(1).max(80),
  size: z.number().int().nonnegative(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const evidencePackageManifestSchema = z.object({
  contract_version: z.literal("kavoosh-evidence-package.v1"),
  authorization_id: z.uuid(),
  collection_request_id: z.uuid(),
  device_id: z.uuid(),
  collector_version: z.string().min(1).max(80),
  collector_build_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  created_at: z.iso.datetime(),
  exclusions: z.array(z.string()).min(4).max(40),
  artifacts: z.array(evidenceArtifactManifestSchema).max(100_000),
});
export type EvidencePackageManifest = z.infer<typeof evidencePackageManifestSchema>;

export const quarantineUploadStartSchema = z.object({
  redemption_id: z.uuid(),
  credential_id: z.uuid(),
  archive_size: z.number().int().positive(),
  archive_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  chunk_size: z.number().int().positive(),
  evidence_manifest: evidencePackageManifestSchema,
  signed_at: z.iso.datetime(),
  nonce: z.string().min(16).max(200),
  signature_b64: z.string().min(80).max(120),
});

export const quarantineUploadOfferSchema = z.object({
  upload_id: z.uuid(),
  contract_version: z.literal("collector-quarantine-upload.v1"),
  upload_token: z.string().min(32).max(200),
  chunk_size: z.number().int().positive(),
  chunk_count: z.number().int().positive(),
  expected_size: z.number().int().positive(),
  expected_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  expires_at: z.iso.datetime(),
});
export type QuarantineUploadOffer = z.infer<typeof quarantineUploadOfferSchema>;

export const quarantineChunkReceiptSchema = z.object({
  upload_id: z.uuid(),
  chunk_index: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  idempotent: z.boolean(),
  received_size: z.number().int().nonnegative(),
});

export const quarantineUploadStatusSchema = z.object({
  upload_id: z.uuid(),
  status: z.enum(["open", "sealed", "admitted", "rejected"]),
  received_size: z.number().int().nonnegative(),
  expected_size: z.number().int().positive(),
  chunk_count: z.number().int().positive(),
  received_chunks: z.array(z.number().int().nonnegative()),
  expires_at: z.iso.datetime(),
  sealed_at: z.iso.datetime().nullable(),
});

export const evidenceAdmissionReceiptSchema = z.object({
  admitted_at: z.iso.datetime(),
  case_id: z.uuid(),
  collection_request_id: z.uuid(),
  contract_version: z.literal("evidence-admission-receipt.v1"),
  device_id: z.uuid(),
  evidence_id: z.uuid(),
  evidence_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  evidence_size: z.number().int().positive(),
  manifest_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  upload_id: z.uuid(),
  // Optional signed v5 extension; historical receipt v1 fields remain intact.
  windows_ram_coverage: z.object({
    authority_generation: z.literal(5),
    coverage_contract_version: z.literal("windows-physical-memory-coverage.v1"),
    status: z.enum(["complete", "partial"]),
    image_size: z.number().int().positive(),
    unreadable_bytes: z.number().int().nonnegative(),
    unreadable_pages: z.number().int().nonnegative().max(8192),
    raw_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    memory_map_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    coverage_manifest_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  }).optional(),
});
export type EvidenceAdmissionReceipt = z.infer<typeof evidenceAdmissionReceiptSchema>;
